import { useState } from 'react'
import type { InteractionProps } from './registry'
import { getNonEmptyString, getScalarString, getString, getStringList } from './shared/config'
import { asRecord } from './shared/response'
import { Feedback } from './shared/Feedback'
import { StaticFallback } from './shared/StaticFallback'
import controls from './shared/controls.module.css'
import styles from './MultipleChoice.module.css'

/**
 * Single-answer multiple choice with immediate, explanatory feedback.
 * One dominant task: pick an option and check it. Retryable.
 */
export function MultipleChoice({
  id,
  config,
  savedResponse,
  onResponseChange,
  onComplete,
}: InteractionProps) {
  const question = getNonEmptyString(config, 'question')
  const options = getStringList(config, 'options')
  const answer = getScalarString(config, 'answer')
  const feedback = getString(config, 'feedback')

  const saved = asRecord(savedResponse)
  const [selected, setSelected] = useState<string | null>(
    typeof saved.selected === 'string' ? saved.selected : null,
  )
  const [checked, setChecked] = useState<boolean>(saved.checked === true)

  const valid = !!(options && options.length >= 2 && answer && options.includes(answer))
  if (!valid) {
    return (
      <StaticFallback title={question ?? 'Multiple choice'}>
        {options ? (
          <ul>
            {options.map((option) => (
              <li key={option}>{option}</li>
            ))}
          </ul>
        ) : (
          <p>This question is unavailable.</p>
        )}
      </StaticFallback>
    )
  }

  const labelId = `${id}-question`
  const isCorrect = checked && selected === answer

  function choose(option: string) {
    if (checked) return
    setSelected(option)
    onResponseChange?.({ selected: option, checked: false })
  }

  function check() {
    if (selected === null) return
    setChecked(true)
    onResponseChange?.({ selected, checked: true })
    if (selected === answer) onComplete?.()
  }

  function retry() {
    setChecked(false)
    onResponseChange?.({ selected, checked: false })
  }

  return (
    <section className={controls.interaction} aria-labelledby={labelId}>
      <p id={labelId} className={controls.prompt}>
        {question}
      </p>
      <div role="radiogroup" aria-labelledby={labelId} className={styles.options}>
        {options!.map((option) => {
          const isSelected = selected === option
          const resultClass = checked
            ? option === answer
              ? styles.correctOption
              : isSelected
                ? styles.wrongOption
                : ''
            : ''
          return (
            <label
              key={option}
              className={`${styles.option} ${isSelected ? styles.selected : ''} ${resultClass}`}
            >
              <input
                type="radio"
                name={id}
                value={option}
                checked={isSelected}
                disabled={checked}
                onChange={() => choose(option)}
              />
              <span>{option}</span>
            </label>
          )
        })}
      </div>

      <div className={controls.actions}>
        {checked ? (
          <button type="button" className={controls.button} onClick={retry}>
            Try again
          </button>
        ) : (
          <button
            type="button"
            className={`${controls.button} ${controls.buttonPrimary}`}
            onClick={check}
            disabled={selected === null}
          >
            Check
          </button>
        )}
      </div>

      {checked && (
        <Feedback tone={isCorrect ? 'correct' : 'incorrect'}>
          <strong>{isCorrect ? 'Correct. ' : 'Not quite. '}</strong>
          {feedback}
        </Feedback>
      )}
    </section>
  )
}
